import { useEffect, useState, useMemo, useRef } from 'react';
import { PointOfInterest } from '../components/SectionBlocks/blocks/Charts/RouteCharts';
import { RichGeopoint } from '../../../types/routeBlockType';
import { RouteData } from '../../../types/mapsApiTypes';
import { TravelMode } from '../../../types/TravelSuggestionType';
import { computeRoute, mapTravelModeToBackend } from '../shared/apiClient/MapsApi';
import { calculatePOIsFromLegs, calculatePOIsFromDistance, createBasicPOIs } from '../utils/maps/poiCalculators';

type Input = {
  routepoints?: RichGeopoint[];
  elevation?: number[];
  travelMode?: TravelMode;
};

type Output = {
  routeData: RouteData | undefined;
  pointsOfInterest: PointOfInterest[];
  error?: Error;
  isLoading: boolean;
};

/**
 * Hook to fetch route data from NAF backend Maps API
 *
 * Note: Error tracking is handled automatically by ApiClient base class
 *
 * @param routepoints Array of waypoints for the route
 * @param elevation Optional elevation profile
 * @param travelMode Travel mode (defaults to CAR/DRIVE)
 * @returns Route data, points of interest, loading state, and error
 */
export const useMapsApiRouteData = ({ routepoints, elevation, travelMode = TravelMode.CAR }: Input): Output => {
  const [routeData, setRouteData] = useState<RouteData | undefined>(undefined);
  const [pointsOfInterest, setPointsOfInterest] = useState<PointOfInterest[]>([]);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Create a unique signature for the route to enable request deduplication
  // Only route parameters (origin, destination, waypoints, travelMode) affect the signature
  // Elevation is NOT included as it's only used for POI calculation, not route computation
  const routeSignature = useMemo(() => {
    if (!routepoints || routepoints.length < 2) return null;

    const coords = routepoints.map((p) => `${p.latlng.lat.toFixed(4)},${p.latlng.lng.toFixed(4)}`).join('|');
    return `${coords}|${travelMode}`;
  }, [routepoints, travelMode]);

  // Track the last fetched signature to prevent duplicate requests
  const lastFetchedSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!routepoints || routepoints.length < 2 || !routeSignature) {
      return;
    }

    // Request deduplication: skip if we've already fetched this exact route
    if (lastFetchedSignatureRef.current === routeSignature) {
      return;
    }

    const fetchRoute = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const waypoints = [...routepoints];
        const start = waypoints.shift();
        const end = waypoints.pop();

        if (!start || !end) {
          throw new Error('Invalid route points');
        }

        // Call backend API
        const data = await computeRoute({
          origin: start.latlng,
          destination: end.latlng,
          waypoints: waypoints.map((wp) => wp.latlng),
          travelMode: mapTravelModeToBackend(travelMode),
        });

        setRouteData(data);

        // Mark this route signature as fetched to prevent duplicate requests
        lastFetchedSignatureRef.current = routeSignature;

        // Calculate points of interest using extracted utility functions
        if (elevation && routepoints) {
          if (data.legs && data.legs.length > 0) {
            // Most accurate: use backend legs data
            const pois = calculatePOIsFromLegs(routepoints, elevation, data);
            setPointsOfInterest(pois);
          } else {
            // Fallback: estimate using total distance
            // NOTE: Less accurate - ideally backend should always return legs
            const pois = calculatePOIsFromDistance(routepoints, elevation, data);
            setPointsOfInterest(pois);
          }
        } else if (routepoints) {
          // No elevation data - use basic POIs
          const pois = createBasicPOIs(routepoints);
          setPointsOfInterest(pois);
        }
      } catch (err) {
        const fetchError = err as Error;
        setError(fetchError);

        // eslint-disable-next-line no-console
        console.error('Failed to fetch route from backend:', fetchError);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSignature, elevation]);
  // Note: routepoints and travelMode are intentionally omitted from deps because:
  // - They are already captured in routeSignature (memoized dependency)
  // - Including them would cause redundant effect triggers
  // - Request deduplication via lastFetchedSignatureRef prevents duplicate fetches

  return { routeData, pointsOfInterest, error, isLoading };
};
